import React, { useMemo, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Copy, Check, Trash2, Eraser } from "lucide-react";
import { toast } from "sonner";
import { fetchAllPages } from "@/lib/serverExport";
import { http } from "@/lib/api";
import { useResourceList } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { IdCell } from "@/components/kit/IdCell";
import { DateRangePicker } from "@/components/kit/DateRangePicker";
import { ClickIdListFilter, parseClickIds, parseOrderIds } from "@/components/kit/ClickIdListFilter";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fmtDateTime, fmtMoney, shortId } from "@/lib/format";
import { useTenantColumn } from "@/hooks/useTenantColumn";
import { useTenants } from "@/hooks/useTenants";
import { CleanLogsDialog } from "@/components/clicks/CleanLogsDialog";

const asOpts = (arr) => (arr || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
const SUB_KEYS = Array.from({ length: 9 }, (_, i) => `sub_id_${i + 1}`);

const CONV_COLORS = { lead: "#38BDF8", sale: "#22C55E", redep: "#2DD4BF", revshare: "#F59E0B", trash: "#EF4444" };

function ConvBadge({ summary }) {
  const s = summary || {};
  if (!s.total) return <span className="text-xs text-muted-foreground/40">—</span>;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="conv-indicator">
      {["sale", "redep", "revshare", "lead", "trash"].filter((k) => s[k] > 0).map((k) => (
        <span key={k} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold"
          style={{ color: CONV_COLORS[k], background: `${CONV_COLORS[k]}1a` }}>
          {k === "revshare" ? "RS" : k === "redep" ? "RD" : k.charAt(0).toUpperCase()}{s[k]}
        </span>
      ))}
    </div>
  );
}

function CopyId({ value }) {
  const [done, setDone] = useState(false);
  if (!value) return <span className="text-muted-foreground/50">—</span>;
  const copy = () => { copyText(value); setDone(true); setTimeout(() => setDone(false), 1200); };
  return (
    <span className="inline-flex max-w-full items-center gap-1.5">
      <span className="break-all font-mono text-xs text-foreground">{value}</span>
      <button onClick={copy} data-testid="copy-id" className="shrink-0 text-muted-foreground hover:text-foreground">
        {done ? <Check className="h-3 w-3 text-[#22C55E]" /> : <Copy className="h-3 w-3" />}
      </button>
    </span>
  );
}

function ClickConversions({ clickId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["click-conversions", clickId],
    queryFn: async () => (await http.get(`/conversions/by-click/${clickId}`)).data,
    enabled: !!clickId,
  });
  const rows = data?.items || [];
  return (
    <Section title={`Conversions for this click${rows.length ? ` (${rows.length})` : ""}`}>
      {isLoading ? <p className="text-xs text-muted-foreground">Loading…</p> :
        rows.length === 0 ? <p className="text-xs text-muted-foreground/60">No conversions yet for this click.</p> : (
        <div className="space-y-1.5" data-testid="click-conversions-list">
          {rows.map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 rounded border border-white/[0.06] bg-black/30 px-2 py-1.5 text-xs">
              <span className="font-mono text-foreground">{c.order_id}</span>
              <span className="flex items-center gap-1.5">
                <StatusBadge status={c.status} /><StatusBadge status={c.payment_status} />
                <span className="font-mono text-muted-foreground">{fmtMoney(c.payout)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/[0.05] py-1.5 last:border-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span className={`max-w-[70%] break-all text-right text-xs ${mono ? "font-mono" : ""} ${value ? "text-foreground" : "text-muted-foreground/50"}`}>{value || "—"}</span>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg border border-white/[0.08] bg-[#0A0A0A] p-3">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70">{title}</p>
      {children}
    </div>
  );
}

function ClickDetail({ click, offerName, streamName, onClose }) {
  if (!click) return null;
  const af = click.antifraud || {};
  const afVal = (v) => (af.checked ? (v ? "Yes" : "No") : "Not checked");
  return (
    <Dialog open={!!click} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl border-white/[0.1] bg-[#141417]" data-testid="click-detail-dialog">
        <DialogHeader><DialogTitle className="font-mono">Click {click.order_id}</DialogTitle></DialogHeader>
        <div className="grid max-h-[70vh] gap-3 overflow-y-auto p-0.5 sm:grid-cols-2">
          <Section title="Tracking">
            <DetailRow label="Order ID" value={<CopyId value={click.order_id} />} />
            <DetailRow label="System ID" value={<CopyId value={click.system_id} />} />
            <DetailRow label="Offer" value={offerName(click.offer_id)} />
            <DetailRow label="Stream" value={streamName(click.stream_id)} />
            <DetailRow label="Status" value={click.status === "duplicate" ? "Duplicate (non-unique)" : "Unique (original)"} />
            {click.status === "duplicate" && click.original_click_id &&
              <DetailRow label="Original Click ID" value={<CopyId value={click.original_click_id} />} />}
            {SUB_KEYS.map((k) => <DetailRow key={k} label={k} value={click[k]} mono />)}
          </Section>
          <div className="space-y-3">
            <ClickConversions clickId={click.click_id || click.system_id || click.order_id} />
            <Section title="Request">
              <DetailRow label="IP" value={click.ip} mono />
              <DetailRow label="Country" value={click.country} mono />
              <DetailRow label="Referrer" value={click.referer} />
              <DetailRow label="Landing URL" value={click.landing_url} />
              <DetailRow label="User Agent" value={click.user_agent} />
            </Section>
            <Section title="Device">
              <DetailRow label="Device" value={click.device} />
              <DetailRow label="Browser" value={click.browser} />
              <DetailRow label="OS" value={click.os} />
              <DetailRow label="ISP" value={click.isp} />
            </Section>
            <Section title="Anti-Fraud">
              <DetailRow label="VPN" value={afVal(af.vpn)} />
              <DetailRow label="Proxy" value={afVal(af.proxy)} />
              <DetailRow label="Datacenter" value={afVal(af.datacenter)} />
              <DetailRow label="Bot score" value={af.checked ? String(af.bot_score ?? "—") : "Not checked"} />
            </Section>
          </div>
          {click.headers && Object.keys(click.headers).length > 0 && (
            <div className="sm:col-span-2">
              <Section title="Headers">
                {Object.entries(click.headers).map(([k, v]) => <DetailRow key={k} label={k} value={String(v)} mono />)}
              </Section>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BulkDeleteFlow({ bulk, setBulk, onDeleted }) {
  const [busy, setBusy] = useState(false);
  if (!bulk) return null;
  const run = async (mode) => {
    setBusy(true);
    try {
      const { data } = await http.post("/clicks/bulk-delete", { ids: bulk.ids, mode });
      toast.success(`Deleted ${data.deleted_clicks} clicks${data.deleted_conversions ? ` + ${data.deleted_conversions} conversions` : ""}${data.kept_with_conversions ? ` (${data.kept_with_conversions} with conversions kept)` : ""}`);
      onDeleted();
      setBulk(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const c = bulk.check || {};
  return (
    <>
      <AlertDialog open={bulk.step === "simple"} onOpenChange={(o) => !o && setBulk(null)}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="bulk-delete-simple-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete selected clicks?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete {c.total} selected click{c.total === 1 ? "" : "s"}? None of them have linked conversions. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-simple-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} data-testid="bulk-delete-simple-confirm" onClick={() => run("clicks_only")}
              className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={bulk.step === "choose"} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent className="max-w-md border-white/[0.1] bg-[#141417]" data-testid="bulk-delete-choose-dialog">
          <DialogHeader>
            <DialogTitle>Linked conversions found</DialogTitle>
            <DialogDescription>
              {c.with_conversions} of the {c.total} selected clicks have {c.conversions} linked conversion{c.conversions === 1 ? "" : "s"}. What should we do?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-1">
            <Button variant="outline" disabled={busy || c.without_conversions === 0} onClick={() => run("clicks_only")}
              data-testid="bulk-delete-clicks-only" className="h-9 justify-start border-white/[0.15] bg-transparent text-xs">
              Delete only clicks without conversions ({c.without_conversions}) — keep clicks with conversions
            </Button>
            <Button disabled={busy} onClick={() => setBulk((b) => ({ ...b, step: "final" }))}
              data-testid="bulk-delete-everything" className="h-9 justify-start bg-[#EF4444] text-xs text-white hover:bg-[#dc2626]">
              Delete everything (including conversions)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={bulk.step === "final"} onOpenChange={(o) => !o && setBulk(null)}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="bulk-delete-final-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete clicks AND conversions?</AlertDialogTitle>
            <AlertDialogDescription>
              The {c.total} selected clicks and all {c.conversions} linked conversions (with their postback and Meta logs) will be permanently deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-final-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={busy} data-testid="bulk-delete-final-confirm" onClick={() => run("everything")}
              className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default function Clicks() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const opts = useOptions();
  const isBuyer = user.role === "buyer";
  const isTenantAdmin = user.role === "tenant_admin";
  const { data: offersData } = useResourceList("offers", {});
  const { data: streamsData } = useResourceList("streams", {});
  const tcol = useTenantColumn("statistics");
  const { data: tenantsData } = useTenants();
  const [detail, setDetail] = useState(null);
  const [bulk, setBulk] = useState(null);
  const [cleanOpen, setCleanOpen] = useState(false);
  const clearSelRef = React.useRef(null);

  // ---- Server-side pagination: DataTablePro reports page/size/applied filters; we fetch one page.
  const [srv, setSrv] = useState({ page: 1, size: 10, q: "", filters: {}, custom: {} });
  const handleServerChange = React.useCallback((s) => setSrv(s), []);
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(srv.size));
    p.set("offset", String((srv.page - 1) * srv.size));
    if (srv.q.trim()) p.set("search", srv.q.trim());
    const f = srv.filters || {}, c = srv.custom || {};
    ["offer_id", "stream_id", "buyer_id", "advertiser_id", "country", "status", "tenant_id"].forEach((k) => {
      const v = f[k];
      if (Array.isArray(v) ? v.length : v && v !== "all") p.set(k, Array.isArray(v) ? v.join(",") : v);
    });
    if (c.dateFrom) p.set("date_from", c.dateFrom);
    if (c.dateTo) p.set("date_to", c.dateTo);
    if (c.subField && (c.subValue || "").trim()) { p.set("sub_field", c.subField); p.set("sub_value", c.subValue.trim()); }
    const ids = parseClickIds(c.clickIds);
    if (ids) p.set("click_ids", [...ids].join(","));
    const oids = parseOrderIds(c.orderIds);
    if (oids) p.set("order_ids", [...oids].join(","));
    if (c.showFiltered) p.set("show_filtered", "1");
    if (srv.sort) { p.set("sort", srv.sort); p.set("order", srv.dir || "asc"); }
    return p.toString();
  }, [srv]);
  const { data, isLoading } = useQuery({
    queryKey: ["clicks-server", qs],
    queryFn: async () => (await http.get(`/clicks?${qs}`)).data,
    placeholderData: (prev) => prev,
  });

  const offers = useMemo(() => offersData?.items || [], [offersData]);
  const streams = useMemo(() => streamsData?.items || [], [streamsData]);
  const offerName = (id) => { const r = data?.items?.find((c) => c.offer_id === id); if (r?.offer_name) return r.offer_name; const o = offers.find((x) => x.id === id); return o ? o.name : (id ? shortId(id) : "—"); };
  const streamName = (id) => { const r = data?.items?.find((c) => c.stream_id === id); if (r?.stream_name) return r.stream_name; const s = streams.find((x) => x.id === id); return s ? (s.name || `Stream #${s.tenant_seq}`) : (id ? shortId(id) : "—"); };

  const subColumns = SUB_KEYS.map((k) => ({
    key: k, label: k.replace("sub_id_", "S"),
    render: (r) => <span className="font-mono text-xs text-muted-foreground">{r[k] || "—"}</span>,
  }));

  const columns = [
    ...(!isBuyer ? [{ key: "system_id", label: "System ID", mono: true, exportValue: (r) => r.system_id, render: (r) => <IdCell value={r.system_id} testId="click-system-id" /> }] : []),
    { key: "order_id", label: "Order ID", mono: true, exportValue: (r) => r.order_id, render: (r) => <IdCell value={r.order_id} strong testId="click-order-id" /> },
    { key: "created_at", label: "Date", sortable: true, render: (r) => fmtDateTime(r.created_at) },
    ...(!isBuyer ? [{ key: "buyer", label: "Buyer", exportValue: (r) => r.buyer_label, render: (r) => <span className="text-foreground/90">{r.buyer_label || r.buyer_name || "—"}</span> }] : []),
    ...(!isBuyer ? [{ key: "advertiser", label: "Advertiser", exportValue: (r) => r.advertiser_name, render: (r) => <span className="text-foreground/90">{r.advertiser_name || "—"}</span> }] : []),
    { key: "offer", label: "Offer", exportValue: (r) => r.offer_name, render: (r) => (
      <div className="flex flex-col">
        <span className="text-foreground/90">{r.offer_name || offerName(r.offer_id)}</span>
        {r.filter_matched && (
          <span className="mt-0.5 inline-flex w-fit items-center gap-1 rounded bg-[#EAB308]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#EAB308]" data-testid={`click-filter-${r.id}`}>
            Filtered → {r.filter_landing_name || (r.filter_action === "landing" ? "Landing" : "Redirect")}
          </span>
        )}
      </div>
    ) },
    { key: "stream", label: "Stream", exportValue: (r) => r.stream_name, render: (r) => <span className="text-foreground/80">{r.stream_name || streamName(r.stream_id)}</span> },
    { key: "country", label: "Country", render: (r) => <span className="font-mono">{r.country || "—"}</span> },
    { key: "ip", label: "IP", mono: true, render: (r) => <span className="text-muted-foreground">{r.ip}</span> },
    { key: "status", label: "Unique", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    { key: "conversions_summary", label: "Conversions", exportValue: (r) => (r.conversions_summary?.total || 0), render: (r) => <ConvBadge summary={r.conversions_summary} /> },
    ...subColumns,
  ];

  const clickRows = data?.items || [];
  const userOpts = useMemo(() => userOptions(opts.users), [opts.users]);
  const offerOptions = useMemo(() => {
    if (isBuyer) {
      // Buyers only see offers they actually worked with — offers with at least one of THEIR
      // streams (any status, incl. paused/disabled). Options are built from own streams.
      const m = new Map();
      streams.forEach((s) => {
        if (s.offer_id && s.offer_name && s.offer_name !== "—" && !m.has(s.offer_id))
          m.set(s.offer_id, { value: s.offer_id, label: s.offer_name, search: `${s.offer_name} ${s.offer_seq ?? ""}` });
      });
      return [...m.values()];
    }
    return offers.map((o) => ({ value: o.id, label: `#${o.tenant_seq} ${o.name}${o.geo ? ` [${String(o.geo).toUpperCase()}]` : ""}`, search: `#${o.tenant_seq} ${o.name} ${o.geo || ""}` }));
  }, [offers, streams, isBuyer]);
  const streamOptions = useMemo(() => streams.map((s) => ({ value: s.id, label: `#${s.tenant_seq} — ${s.name || "Stream"}`, search: `#${s.tenant_seq} ${s.tenant_seq} ${s.name || ""} ${s.stream_key || ""}` })), [streams]);
  const advertiserOptions = useMemo(() => (opts.advertisers || []).map((a) => ({ value: a.id, label: `#${a.tenant_seq ?? ""} ${a.name}`.trim(), search: `#${a.tenant_seq ?? ""} ${a.tenant_seq ?? ""} ${a.name}` })), [opts.advertisers]);

  const filters = [
    ...(tcol.show ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
    { key: "offer_id", label: "Offer", multi: true, getValue: (r) => r.offer_id, options: offerOptions },
    { key: "stream_id", label: "Stream", multi: true, getValue: (r) => r.stream_id, options: streamOptions },
    ...(!isBuyer ? [{ key: "buyer_id", label: "Buyer", multi: true, getValue: (r) => r.buyer_id, options: userOpts }] : []),
    ...(!isBuyer ? [{ key: "advertiser_id", label: "Advertiser", multi: true, getValue: (r) => r.advertiser_id, options: advertiserOptions }] : []),
    { key: "country", label: "Country", country: true, multi: true, getValue: (r) => r.country },
    { key: "status", label: "Unique", multi: true, getValue: (r) => r.status, options: asOpts([{ value: "unique", label: "Unique" }, { value: "duplicate", label: "Non-unique" }]) },
  ];

  const startBulkDelete = async (ids, clear) => {
    clearSelRef.current = clear;
    try {
      const { data: check } = await http.post("/clicks/bulk-delete", { ids, mode: "check" });
      if (!check.total) return toast.error("Selected clicks were not found");
      setBulk({ ids, check, step: check.with_conversions > 0 ? "choose" : "simple" });
    } catch (e) { toast.error(e.response?.data?.detail || "Check failed"); }
  };
  const onDeleted = () => {
    qc.invalidateQueries({ queryKey: ["clicks-server"] });
    qc.invalidateQueries({ queryKey: ["conversions"] });
    clearSelRef.current?.();
  };

  const rowActions = !isBuyer ? (row) => [{ label: "Click details", icon: Eye, onClick: () => setDetail(row), testId: `view-click-${row.id}` }] : undefined;

  return (
    <div className="space-y-5">
      <PageHeader title="Clicks" subtitle={isBuyer ? "Your click log — search by Order ID and reconcile with your tracker." : "Raw click log with full technical detail."} />
      <DataTablePro testId="clicks-table" columns={tcol.inject(columns)} rows={clickRows} loading={isLoading && !data}
        serverMode serverTotal={data?.total} onServerChange={handleServerChange} fetchAllRows={() => fetchAllPages("/clicks", qs)}
        filters={filters} searchKeys={isBuyer ? ["order_id"] : ["order_id", "system_id", "buyer_name", "buyer_label"]}
        searchPlaceholder={isBuyer ? "Search by Order ID..." : "Search by Order ID or System ID..."}
        exportName="clicks" rowActions={rowActions} emptyText="No clicks recorded."
        selectable={isTenantAdmin} bulkActions={[]}
        bulkExtra={isTenantAdmin ? ({ ids, clear }) => (
          <Button size="sm" onClick={() => startBulkDelete(ids, clear)} data-testid="clicks-bulk-delete-selected"
            className="h-8 gap-1.5 bg-[#EF4444] text-xs text-white hover:bg-[#dc2626]"><Trash2 className="h-3.5 w-3.5" /> Delete Selected</Button>
        ) : undefined}
        toolbarRight={isTenantAdmin && (
          <Button variant="outline" onClick={() => setCleanOpen(true)} data-testid="clicks-clean-logs-btn"
            className="h-9 gap-1.5 rounded-lg border-white/[0.1] text-sm"><Eraser className="h-3.5 w-3.5" /><span className="hidden min-[1460px]:inline">Clean Logs</span></Button>
        )}
        customFilters={({ values, set }) => (
          <div className="w-full sm:w-[190px]">
            <DateRangePicker from={values.dateFrom} to={values.dateTo}
              onChange={({ from, to }) => { set("dateFrom", from); set("dateTo", to); }}
              placeholder="Date range" testId="clicks-date-range" />
          </div>
        )}
        customFiltersEnd={({ values, set, onEnter }) => (
          <>
            <div className="w-full sm:w-[150px]">
              <Select value={values.subField || "none"} onValueChange={(v) => set("subField", v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-sm" data-testid="clicks-sub-field"><SelectValue placeholder="Sub ID field" /></SelectTrigger>
                <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                  <SelectItem value="none">Sub ID field</SelectItem>
                  {SUB_KEYS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input value={values.subValue || ""} onChange={(e) => set("subValue", e.target.value)} onKeyDown={onEnter}
              placeholder="Sub ID value" className="h-9 w-full rounded-lg border-white/[0.1] bg-[#0A0A0A] text-sm sm:w-[150px]" data-testid="clicks-sub-value" />
            {!isBuyer && <ClickIdListFilter value={values.clickIds || ""} onChange={(v) => set("clickIds", v)} testId="clicks-clickids" />}
            <ClickIdListFilter value={values.orderIds || ""} onChange={(v) => set("orderIds", v)} testId="clicks-orderids"
              label="Order IDs" title="Filter by Order ID" parse={parseOrderIds} />
            <label className="flex h-9 cursor-pointer select-none items-center gap-2 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-3 text-sm text-muted-foreground transition-colors hover:border-white/20">
              <Checkbox checked={!!values.showFiltered} onCheckedChange={(v) => set("showFiltered", !!v)} data-testid="clicks-show-filtered" />
              Show filtered
            </label>
          </>
        )} />
      <ClickDetail click={detail} offerName={offerName} streamName={streamName} onClose={() => setDetail(null)} />
      <BulkDeleteFlow bulk={bulk} setBulk={setBulk} onDeleted={onDeleted} />
      {isTenantAdmin && (
        <CleanLogsDialog open={cleanOpen} onOpenChange={setCleanOpen}
          offerOptions={offerOptions} streamOptions={streamOptions} buyerOptions={userOpts}
          advertiserOptions={advertiserOptions} currentFilters={{ filters: srv.filters, custom: srv.custom }} />
      )}
    </div>
  );
}
